/**
 * ヘッダーコンポーネント
 * ナビゲーション、テーマ切替、言語切替を含む
 */
import { Link, NavLink } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Database, Search, FlaskConical, Atom, Menu, Moon, Sun, Globe } from 'lucide-react'
import { useState } from 'react'
import { useThemeStore } from '@/stores/themeStore'
import { useTranslation } from '@/hooks/useTranslation'

/** ナビゲーション項目（翻訳キーを使用） */
const getNavigation = (t: (key: string) => string) => [
  { name: t('nav.clusters'), href: '/clusters', icon: Database },
  { name: t('nav.blast'), href: '/blast', icon: Search },
  { name: t('nav.reactions'), href: '/reactions', icon: FlaskConical },
  { name: t('nav.substructure'), href: '/substructure', icon: Atom },
]

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { isDark, toggle } = useThemeStore()
  const { t, locale, setLocale } = useTranslation()

  // 翻訳されたナビゲーション項目を取得
  const navigation = getNavigation(t)

  /** 言語を切り替える */
  const toggleLocale = () => {
    setLocale(locale === 'en' ? 'ja' : 'en')
  }

  return (
    <header className="sticky top-0 z-50 backdrop-blur-glass border-b border-white/5">
      <nav className="container mx-auto px-4 max-w-7xl">
        <div className="flex items-center justify-between h-16">
          {/* ロゴ */}
          <Link to="/" className="flex items-center gap-3">
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="w-10 h-10 rounded-xl bg-gradient-primary flex items-center justify-center"
            >
              <Database className="w-5 h-5 text-white" />
            </motion.div>
            <h1 className="text-lg font-bold gradient-text">BGC Proteins</h1>
          </Link>

          {/* デスクトップナビゲーション */}
          <div className="hidden md:flex items-center gap-1">
            {navigation.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-white/10 text-accent-cyan'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`
                }
              >
                <item.icon className="w-4 h-4" />
                {item.name}
              </NavLink>
            ))}
          </div>

          {/* 右側ボタン群 */}
          <div className="flex items-center gap-3">
            {/* 言語切替ボタン */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={toggleLocale}
              className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-white/5 transition-colors text-sm"
              aria-label="Toggle language"
            >
              <Globe className="w-4 h-4 text-gray-400" />
              <span className="text-gray-400 font-medium uppercase">{locale}</span>
            </motion.button>

            {/* テーマ切替ボタン */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={toggle}
              className="p-2 rounded-lg hover:bg-white/5 transition-colors"
              aria-label="Toggle theme"
            >
              {isDark ? (
                <Sun className="w-5 h-5 text-gray-400" />
              ) : (
                <Moon className="w-5 h-5 text-gray-400" />
              )}
            </motion.button>

            {/* モバイルメニューボタン */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-white/5"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* モバイルナビゲーション */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden py-4 border-t border-white/5"
          >
            {navigation.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium ${
                    isActive
                      ? 'bg-white/10 text-accent-cyan'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`
                }
              >
                <item.icon className="w-4 h-4" />
                {item.name}
              </NavLink>
            ))}
          </motion.div>
        )}
      </nav>
    </header>
  )
}
